import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';
import { useSelection } from '@/contexts/SelectionContext';
import { useProductStats, type ProductStats } from '@/hooks/useProductStats';

export const LIVE_FEED_STATUS_QUERY_KEY = 'live-feed-status' as const;

/**
 * Der Stand unseres Supplemental Feeds bei Google, soweit WIR ihn kennen.
 *
 * Genau drei Zeitpunkte sind belegbar und keiner davon heisst "Google hat
 * gelesen": `last_upload_at` ist unser Upload, `feeds.last_synced_at` unser
 * Lesezugriff auf die Merchant API, und `status` ist der Zustand der
 * Feed-Quelle, den das Merchant Center uns zurueckmeldet. Einen Zeitpunkt, zu
 * dem Google unseren Supplemental Feed VERARBEITET hat, gibt es in unseren
 * Daten nicht -- deshalb darf das Kapitel "Bei Google" ihn auch nicht
 * behaupten (Spec Abschnitt 6, "Ehrlichkeit der Beschriftung").
 *
 * `products_uploaded` wird bewusst NICHT angezeigt, obwohl die Spalte hier
 * mitgelesen wird: die live liegende Menge kommt aus `useProductStats`
 * (`uploaded + needsReupload`), weil dieselbe Zahl die Chips speist. Zwei
 * Quellen fuer "wie viele sind live" waeren zwei Zahlen, die auseinanderlaufen,
 * sobald ein Upload teilweise scheitert.
 */
export interface LiveFeedRow {
  status: string;
  last_upload_at: string | null;
  products_uploaded: number | null;
  error_message: string | null;
}

export interface LiveFeedStatus {
  /** `null` heisst "es gibt noch keinen Supplemental Feed", nicht "Fehler". */
  row: LiveFeedRow | null;
  isLoading: boolean;
  isError: boolean;
  /** Unser letzter Lesezugriff auf die Merchant API (`feeds.last_synced_at`). */
  lastSyncedAt: string | null;
  stats: ProductStats;
  /** Die Feed-ID, die `stats` tatsaechlich gemessen hat (Muster `useProductStats`). */
  statsFeedId: string | null;
}

/**
 * Liest die eine `supplemental_feeds`-Zeile des Feeds plus die gemessenen
 * Produktzahlen. Kein neuer Backend-Pfad (Spec Abschnitt 6): eine Abfrage auf
 * eine bestehende Tabelle, alles Weitere kommt aus Hooks, die es schon gibt.
 */
export function useLiveFeedStatus(feedId: string | null): LiveFeedStatus {
  const { feed } = useSelection();
  const { stats, statsFeedId } = useProductStats(feedId, false, null, null);

  const query = useQuery({
    queryKey: [LIVE_FEED_STATUS_QUERY_KEY, feedId],
    enabled: !!feedId,
    staleTime: 60_000,
    queryFn: async (): Promise<LiveFeedRow | null> => {
      const { data, error } = await supabase
        .from('supplemental_feeds')
        .select('status, last_upload_at, products_uploaded, error_message')
        .eq('feed_id', feedId as string)
        .maybeSingle();
      if (error) {
        Sentry.captureException(error, {
          tags: { feature: 'live-chapter', hook: 'useLiveFeedStatus' },
        });
        throw error;
      }
      return (data as LiveFeedRow | null) ?? null;
    },
  });

  return {
    // `?? null` und nicht `query.data`: bei einem Fehler ist `data` undefined,
    // und `undefined` liest sich an der Verbrauchsstelle wie "noch nicht
    // geladen". Das Kapitel unterscheidet Fehler (`isError`) von "keine Zeile"
    // (`row === null`) ausdruecklich.
    row: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    lastSyncedAt: feed?.last_synced_at ?? null,
    stats,
    statsFeedId,
  };
}
